import React from 'react';
import { Typography } from 'antd';

const { Title, Text } = Typography;

export const AuthHeroBadge: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
  <span
    className="auth-slide-in-left mb-4 inline-flex items-center gap-2 self-start rounded-full border border-white/25 bg-white/10 px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/95 backdrop-blur-md sm:mb-5 sm:px-4 sm:text-xs"
    style={{ letterSpacing: '0.1em' }}
  >
    <span className="text-[#F7941F] [&_.anticon]:text-sm sm:[&_.anticon]:text-base">{icon}</span>
    {children}
  </span>
);

export const AuthHeroTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Title
    level={1}
    className="auth-slide-in-left !mb-3 !mt-0 !text-2xl !font-bold !text-white sm:!mb-4 sm:!text-3xl lg:!mb-5 lg:!text-4xl xl:!text-[2.35rem]"
    style={{
      textShadow: '0 2px 16px rgba(0, 0, 0, 0.2)',
      fontFamily: 'Poppins, sans-serif',
      lineHeight: 1.2,
    }}
  >
    {children}
  </Title>
);

export const AuthHeroDescription: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text className="auth-slide-in-left-delay-1 !mb-4 !mt-0 block max-w-prose !text-sm !leading-relaxed !text-white/88 sm:!mb-5 sm:!text-base lg:!mb-6">
    {children}
  </Text>
);

export const AuthHeroHighlights: React.FC<{ items: { value: string; label: string }[] }> = ({ items }) => (
  <div className="auth-slide-in-left-delay-1 mb-4 grid grid-cols-3 gap-3 sm:mb-5 sm:gap-4 lg:mb-6">
    {items.map((item) => (
      <div
        key={item.label}
        className="rounded-lg border border-white/20 bg-white/10 px-3 py-2.5 backdrop-blur-md sm:px-3.5 sm:py-3"
      >
        <div className="text-sm font-bold text-white sm:text-base">{item.value}</div>
        <div className="mt-0.5 text-[10px] font-medium leading-snug text-white/75 sm:text-xs">{item.label}</div>
      </div>
    ))}
  </div>
);

export const AuthHeroFeatureHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="auth-slide-in-left-delay-2 mb-3 text-[11px] font-semibold uppercase tracking-wide text-white/80 md:mb-3 lg:mb-3">
    {children}
  </p>
);

export const AuthHeroFeatureList: React.FC<{
  items: { icon: React.ReactNode; title: string; description: string }[];
}> = ({ items }) => (
  <ul className="m-0 grid list-none grid-cols-1 gap-2.5 p-0 sm:grid-cols-2 sm:gap-3 md:gap-3 lg:grid xl:gap-3.5">
    {items.map((feature) => (
      <li
        key={feature.title}
        className="auth-slide-in-left-delay-2 flex gap-3 rounded-lg border border-white/15 bg-black/10 px-3 py-2.5 backdrop-blur-sm xl:px-3.5 xl:py-3"
      >
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/25 bg-white/20 text-base text-white">
          {feature.icon}
        </div>
        <div className="min-w-0">
          <div className="text-xs font-semibold leading-snug text-white xl:text-[13px]">{feature.title}</div>
          <Text className="!mb-0 !mt-1 block !text-[11px] !leading-snug !text-white/72 xl:!text-[12px]">
            {feature.description}
          </Text>
        </div>
      </li>
    ))}
  </ul>
);
